// =============================================================================
// hooks/useKpi.js
//
// Hooks that abstract Redux away from the components.
//
// FIX5 — incident filtering is server-side, but pagination is NOT anymore.
// `items` holds every row matching the current filters (up to a maxRows
// safety cap, see incidentActions.fetchIncidents) — the table paginates
// that already-small, already-filtered set in memory. `totalCount` is the
// true match count from the server (even when `items` was capped), used
// only to warn the operator if the result was truncated. Tech/KPI/Vendor
// dropdown OPTIONS come from `useLookups()` (the full rule/vendor
// universe), not from `items` — see IncidentFilters.jsx.
//
// Loading is gated behind an explicit "Apply" button (see
// IncidentDashboard) rather than firing automatically on every filter
// change — `updateFilters`/`clearFilters` just update the filter state;
// nothing is fetched until the caller explicitly calls `load()`.
// =============================================================================
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import {
	fetchIncidents, fetchIncidentHistory, selectIncident, changeFilter, resetFilters,
} from '../actions/incidentActions';
import {
	fetchRules, saveRule, deleteRule, selectRule, resetRuleForm,
	validateFormula, resetValidation, fetchEngineLogs,
} from '../actions/kpiRuleActions';
import { fetchLookups } from '../actions/lookupActions';
import { fetchSites } from '../actions/siteActions';

// ============================================================================
// useLookups — dropdown data from the DB (vendors, techs, kpis, severities, statuses)
// ============================================================================
export function useLookups() {
	const dispatch = useDispatch();

	const lookups = useSelector(function (s) { return s.lookups; });

	const load = useCallback(function () {
		return dispatch(fetchLookups());
	}, [dispatch]);

	// Helper maps (O(1) lookup by value)
	const severityMap = useMemo(function () {
		const map = {};
		(lookups.severities || []).forEach(function (s) { map[s.value] = s; });
		return map;
	}, [lookups.severities]);

	const statusMap = useMemo(function () {
		const map = {};
		(lookups.statuses || []).forEach(function (s) { map[s.value] = s; });
		return map;
	}, [lookups.statuses]);

	return {
		loading:     lookups.loading,
		loaded:      lookups.loaded,
		error:       lookups.error,
		vendors:     lookups.vendors,
		techs:       lookups.techs,
		kpis:        lookups.kpis,
		severities:  lookups.severities,
		statuses:    lookups.statuses,
		severityMap: severityMap,
		statusMap:   statusMap,
		load:        load,
	};
}

// ============================================================================
// useIncidents
//
// Filtering happens server-side; pagination does NOT (see fix5 note above).
// `items` is the whole (filtered, capped) result set.
// ============================================================================
export function useIncidents() {
	const dispatch = useDispatch();

	const items          = useSelector(function (s) { return s.incidents.items; });
	const totalCount     = useSelector(function (s) { return s.incidents.totalCount; });
	const loading        = useSelector(function (s) { return s.incidents.loading; });
	const loaded         = useSelector(function (s) { return s.incidents.loaded; });
	const error          = useSelector(function (s) { return s.incidents.error; });
	const selected       = useSelector(function (s) { return s.incidents.selected; });
	const history        = useSelector(function (s) { return s.incidents.history; });
	const historyLoading = useSelector(function (s) { return s.incidents.historyLoading; });
	const filters        = useSelector(function (s) { return s.incidents.filters; });

	// --- Actions ---------------------------------------------------------
	// Fetches everything matching the current filters — reads them straight
	// from the store (see incidentActions.fetchIncidents), so it behaves
	// the same regardless of what triggered it (Apply button, etc).
	const load = useCallback(function () {
		return dispatch(fetchIncidents());
	}, [dispatch]);

	const select = useCallback(function (inc) {
		dispatch(selectIncident(inc));
		if (inc && inc.incidentId) {
			dispatch(fetchIncidentHistory(inc.incidentId));
		}
	}, [dispatch]);

	// Only updates the filter STATE — does not fetch. The caller (Apply
	// button) decides when to actually call load().
	const updateFilters = useCallback(function (f) {
		dispatch(changeFilter(f));
	}, [dispatch]);

	const clearFilters = useCallback(function () {
		dispatch(resetFilters());
	}, [dispatch]);

	return {
		items: items,
		// `filteredItems` kept as an alias for backward compatibility with
		// components that still read it — items ARE already filtered.
		filteredItems: items,
		totalCount: totalCount,
		loading: loading,
		loaded: loaded,
		error: error,
		selected: selected,
		history: history,
		historyLoading: historyLoading,
		filters: filters,
		load: load,
		select: select,
		updateFilters: updateFilters,
		clearFilters: clearFilters,
	};
}

// ============================================================================
// useKpiRules
// ============================================================================
export function useKpiRules() {
	const dispatch = useDispatch();

	const items             = useSelector(function (s) { return s.kpiRules.items; });
	const loading           = useSelector(function (s) { return s.kpiRules.loading; });
	const error             = useSelector(function (s) { return s.kpiRules.error; });
	const selectedRule      = useSelector(function (s) { return s.kpiRules.selectedRule; });
	const saving            = useSelector(function (s) { return s.kpiRules.saving; });
	const saveError         = useSelector(function (s) { return s.kpiRules.saveError; });
	const saveSuccess       = useSelector(function (s) { return s.kpiRules.saveSuccess; });
	const deleting          = useSelector(function (s) { return s.kpiRules.deleting; });
	const validation        = useSelector(function (s) { return s.kpiRules.validation; });
	const engineLogs        = useSelector(function (s) { return s.kpiRules.engineLogs; });
	const engineLogsLoading = useSelector(function (s) { return s.kpiRules.engineLogsLoading; });

	const load     = useCallback(function ()     { return dispatch(fetchRules()); },         [dispatch]);
	const save     = useCallback(function (rule) { return dispatch(saveRule(rule)); },       [dispatch]);
	const remove   = useCallback(function (id)   { return dispatch(deleteRule(id)); },       [dispatch]);
	const select   = useCallback(function (rule) { return dispatch(selectRule(rule)); },     [dispatch]);
	const openNew  = useCallback(function ()     { return dispatch(resetRuleForm()); },      [dispatch]);
	const validate = useCallback(function (p)    { return dispatch(validateFormula(p)); },   [dispatch]);
	const clearVal = useCallback(function ()     { return dispatch(resetValidation()); },    [dispatch]);
	const loadLogs = useCallback(function ()     { return dispatch(fetchEngineLogs()); },    [dispatch]);

	const rulesByTech = useMemo(function () {
		return items.reduce(function (acc, r) {
			if (!acc[r.tech]) acc[r.tech] = [];
			acc[r.tech].push(r);
			return acc;
		}, {});
	}, [items]);

	return {
		items: items,
		loading: loading,
		error: error,
		selectedRule: selectedRule,
		saving: saving,
		saveError: saveError,
		saveSuccess: saveSuccess,
		deleting: deleting,
		validation: validation,
		engineLogs: engineLogs,
		engineLogsLoading: engineLogsLoading,
		rulesByTech: rulesByTech,
		load: load,
		save: save,
		remove: remove,
		select: select,
		openNew: openNew,
		validate: validate,
		clearVal: clearVal,
		loadLogs: loadLogs,
	};
}

//
// useSites — sites with coordinates for the map. Also exposes a Map() for
// O(1) lookup by "vendor|siteId" key (used in IncidentMap).
// =============================================================================
export function useSites() {
	const dispatch = useDispatch();

	const sites = useSelector(function (s) { return s.sites; });

	const load = useCallback(function () {
		return dispatch(fetchSites());
	}, [dispatch]);

	const siteMap = useMemo(function () {
		const map = new Map();
		(sites.items || []).forEach(function (s) {
			map.set(s.vendor + '|' + s.siteId, s);
		});
		return map;
	}, [sites.items]);

	return {
		items:   sites.items,
		loading: sites.loading,
		loaded:  sites.loaded,
		error:   sites.error,
		siteMap: siteMap,
		load:    load,
	};
}